/**
 * Calendar days as `YYYY-MM-DD` strings. A tech's day is computed in their own
 * timezone (BUILD_PLAN.md section 17.1), so "today" always takes a zone.
 */

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

// Making an Intl.DateTimeFormat costs about twenty times what using one does,
// and the day view formats a time for every block and activity it draws:
// thousands on a busy day (roadmap PR-70). So each format is made once, for
// its locale and options, and kept.
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale} ${JSON.stringify(options)}`;
  let found = formatters.get(key);
  if (found === undefined) {
    found = new Intl.DateTimeFormat(locale, options);
    formatters.set(key, found);
  }
  return found;
}

/** Today's date in `timeZone` (an IANA name), as YYYY-MM-DD. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  // en-CA formats dates as YYYY-MM-DD.
  return formatter("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** True for a real calendar date in YYYY-MM-DD form. */
export function isIsoDay(value: string): boolean {
  const match = ISO_DAY.exec(value);
  if (!match) {
    return false;
  }
  const [, y, m, d] = match.map(Number) as [number, number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/** The day `days` after `day` (negative for before). Pure calendar arithmetic. */
export function addDays(day: string, days: number): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().slice(0, 10);
}

/** "Mon, Sep 28": the short form in the header (section 11.0 mockup). */
export function formatShortDay(day: string): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return formatter("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** "Mon" and "Sep 28": a day in two short lines, as the team overview's columns head them. */
export function formatDayParts(day: string): { weekday: string; date: string } {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const at = new Date(Date.UTC(y, m - 1, d));
  const format = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat("en-US", { ...options, timeZone: "UTC" }).format(at);
  return { weekday: format({ weekday: "short" }), date: format({ month: "short", day: "numeric" }) };
}

/** "Monday, September 28, 2026": the long form, for headings and labels. */
export function formatLongDay(day: string): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return formatter("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** The UTC offset of `timeZone` at the instant `utc`, in minutes east of UTC. */
function offsetMinutes(utc: Date, timeZone: string): number {
  const parts = formatter("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(utc);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
  return Math.round((asUtc - utc.getTime()) / 60000);
}

/**
 * The instant a wall-clock time ("YYYY-MM-DDTHH:MM", as an
 * <input type="datetime-local"> gives it) names in `timeZone`.
 */
export function zonedToUtc(local: string, timeZone: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!match) {
    throw new Error(`not a YYYY-MM-DDTHH:MM time: ${local}`);
  }
  const [, y, mo, d, h, mi] = match.map(Number) as [number, number, number, number, number, number];
  const wall = Date.UTC(y, mo - 1, d, h, mi);
  // Two passes settle the offset, including next to a DST change.
  let utc = new Date(wall - offsetMinutes(new Date(wall), timeZone) * 60000);
  utc = new Date(wall - offsetMinutes(utc, timeZone) * 60000);
  return utc;
}

/** An instant as a date and time in `timeZone`, e.g. "Oct 1, 2026, 9:00 AM". */
export function formatDateTimeIn(iso: string, timeZone: string): string {
  return formatter("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone,
  }).format(new Date(iso));
}

/** The wall-clock hour (0–23) and minute of an instant in `timeZone`. */
export function clockIn(iso: string, timeZone: string): { hour: number; minute: number } {
  const parts = formatter("en-US", {
    timeZone,
    hourCycle: "h23",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(new Date(iso));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { hour: get("hour"), minute: get("minute") };
}

/** "9:40", with no AM/PM: the compact form on cards and rows (section 11.0 mockup). */
export function formatClockIn(iso: string, timeZone: string): string {
  const { hour, minute } = clockIn(iso, timeZone);
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(twelve)}:${String(minute).padStart(2, "0")}`;
}

/** "9:40 AM": the full form, for labels read aloud and hover text. */
export function formatTimeIn(iso: string, timeZone: string): string {
  const { hour } = clockIn(iso, timeZone);
  return `${formatClockIn(iso, timeZone)} ${hour < 12 ? "AM" : "PM"}`;
}

/** "9:40–10:36", or just "10:36" for an instant with no end. */
export function formatClockRange(start: string, end: string | null, timeZone: string): string {
  const from = formatClockIn(start, timeZone);
  return end === null ? from : `${from}–${formatClockIn(end, timeZone)}`;
}

/** "9:40 AM to 10:36 AM": the range read aloud. */
export function formatTimeRange(start: string, end: string | null, timeZone: string): string {
  const from = formatTimeIn(start, timeZone);
  return end === null ? from : `${from} to ${formatTimeIn(end, timeZone)}`;
}
